import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiClient } from '../../lib/api'
import { FoliateViewer } from './FoliateViewer'
import { Button } from '../ui/button'
import { Loader2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DownloadLimitDialog } from '../membership/DownloadLimitDialog'
import type { MembershipTier } from '../../types'
import { useNavigateBack } from '../../hooks/useNavigateBack'

export function ReaderPage() {
  const { t } = useTranslation('common')
  const { bookId, translationId } = useParams<{ bookId: string; translationId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const goBack = useNavigateBack('/library')
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [streamUrl, setStreamUrl] = useState<string | null>(null)
  const [translationLanguage, setTranslationLanguage] = useState('')
  const [cefrLevel, setCefrLevel] = useState<string | null>(null)
  const [fileFormat, setFileFormat] = useState<string>('epub')
  
  // Saved progress state
  const [savedProgress, setSavedProgress] = useState<number | undefined>(undefined)
  const [savedPosition, setSavedPosition] = useState<string | null>(null)
  
  // Reader settings from user profile
  const [readerFontSize, setReaderFontSize] = useState<number | undefined>(undefined)
  const [readerTheme, setReaderTheme] = useState<'light' | 'dark' | 'sepia' | undefined>(undefined)
  
  // Progress save debouncing
  const saveProgressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  
  // Download limit dialog state
  const [showLimitDialog, setShowLimitDialog] = useState(false)
  const [limitInfo, setLimitInfo] = useState<{ used: number; limit: number; tier: MembershipTier }>({ 
    used: 0, 
    limit: 0, 
    tier: 'basic' 
  })

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: `/read/${bookId}/${translationId}` } })
      return
    }

    if (!bookId || !translationId) {
      setError(t('reader.invalidParams', 'Invalid book or translation ID'))
      setLoading(false)
      return
    }

    startReading()
    loadUserReaderSettings()
  }, [bookId, translationId, user, navigate, t])

  const loadUserReaderSettings = async () => {
    try {
      const response = await apiClient.preferences.getUserPreferences()
      if (response.preferences) {
        const { reader_font_size, reader_theme } = response.preferences
        if (reader_font_size && typeof reader_font_size === 'number') {
          setReaderFontSize(reader_font_size)
        }
        if (reader_theme && ['light', 'dark', 'sepia'].includes(reader_theme)) {
          setReaderTheme(reader_theme as 'light' | 'dark' | 'sepia')
        }
      }
    } catch (err) {
      console.error('Failed to load reader settings:', err)
    }
  }

  const startReading = async () => {
    if (!bookId || !translationId) return

    try {
      setLoading(true)
      setError(null)

      const response = await apiClient.reader.startSession(bookId, translationId)

      if (!response.success) {
        if (response.error_code === 'DOWNLOAD_LIMIT_REACHED') {
          // Fetch current membership to get tier
          try {
            const membership = await apiClient.membership.getMembership()
            setLimitInfo({
              used: response.downloads_used || 0,
              limit: response.downloads_limit || 0,
              tier: membership.membership?.tier || 'basic'
            })
            setShowLimitDialog(true)
          } catch (e) {
            setLimitInfo({
              used: response.downloads_used || 0,
              limit: response.downloads_limit || 0,
              tier: 'basic'
            })
            setShowLimitDialog(true)
          }
          setLoading(false)
          return
        }
        
        setError(response.error || t('reader.startError', 'Failed to start reading session'))
        setLoading(false)
        return
      }

      setStreamUrl(response.stream_url ?? null)
      setTranslationLanguage(response.translation_language ?? '')
      setCefrLevel(response.cefr_level ?? null)
      setFileFormat(response.file_format ?? 'epub')
      setSavedProgress(response.saved_progress ?? undefined)
      setSavedPosition(response.saved_position ?? null)
      setLoading(false)
    } catch (err) {
      console.error('Failed to start reading:', err)
      setError(t('reader.startError', 'Failed to start reading session'))
      setLoading(false)
    }
  }

  const handleClose = () => {
    // Navigate back using browser history (preserves filters, scroll position, etc.)
    goBack()
  }

  const handleLimitDialogClose = () => {
    setShowLimitDialog(false)
    handleClose()
  }

  // Save progress to server (debounced)
  const handleProgressChange = useCallback((progress: number, position: string | null) => {
    if (!translationId) return
    
    // Clear any pending save
    if (saveProgressTimeoutRef.current) {
      clearTimeout(saveProgressTimeoutRef.current)
    }
    
    // Debounce save - only save after 3 seconds of no navigation
    // This prevents excessive API calls when user rapidly flips through pages
    saveProgressTimeoutRef.current = setTimeout(() => {
      apiClient.reader.updateProgress(translationId, progress, position)
        .catch(err => console.error('Failed to save reading progress:', err))
    }, 2000)
  }, [translationId])
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveProgressTimeoutRef.current) {
        clearTimeout(saveProgressTimeoutRef.current)
      }
    }
  }, [])

  if (!user) {
    return null // Will redirect in useEffect
  }

  if (showLimitDialog) {
    return (
      <div className="reader-page fixed inset-0 z-50 bg-background overflow-hidden">
        <DownloadLimitDialog
          open={showLimitDialog}
          onClose={handleLimitDialogClose}
          onUpgraded={() => {
            setShowLimitDialog(false)
            startReading()
          }}
          currentTier={limitInfo.tier}
          downloadsUsed={limitInfo.used}
          downloadsLimit={limitInfo.limit}
        />
      </div>
    )
  }

  if (loading) {
    return (
      <div className="reader-page fixed inset-0 z-50 bg-background overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">{t('reader.preparing', 'Preparing your book...')}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="reader-page fixed inset-0 z-50 bg-background overflow-hidden flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">{t('reader.errorTitle', 'Unable to Open Book')}</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={handleClose}>
            {t('common.goBack', 'Go Back')}
          </Button>
        </div>
      </div>
    )
  }

  if (!streamUrl) {
    return (
      <div className="reader-page fixed inset-0 z-50 bg-background overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">{t('reader.noContent', 'No content available')}</p>
          <Button onClick={handleClose} className="mt-4">
            {t('common.goBack', 'Go Back')}
          </Button>
        </div>
      </div>
    )
  }

  // Construct full URL for epub
  const fullStreamUrl = `${window.location.origin}${streamUrl}`

  return (
    <div className="reader-page fixed inset-0 z-50 bg-background overflow-hidden flex flex-col">
      {/* Reader viewer - full height, FoliateViewer has its own header */}
      <div className="reader-viewer flex-1 overflow-hidden">
        <FoliateViewer
          url={fullStreamUrl}
          fileFormat={fileFormat}
          showControls={true}
          onClose={handleClose}
          initialProgress={savedProgress}
          initialPosition={savedPosition}
          onProgressChange={handleProgressChange}
          bookLanguage={translationLanguage}
          bookLevel={cefrLevel || undefined}
          initialFontSize={readerFontSize}
          initialTheme={readerTheme}
        />
      </div>
    </div>
  )
}
